import { useState, useEffect, useRef } from 'react';
import { useNetworkStatus } from '../../services/networkService';

/**
 * Floating toast banner shown when the device goes offline.
 * - Slides down when connectivity is lost, auto-dismisses after 5 seconds
 * - Shows brief "Back online" message when restored, then slides up
 * - Does NOT block interaction
 */
export default function OfflineBanner() {
  const { online } = useNetworkStatus();
  // `mounted` keeps the toast in the DOM for the 300ms slide-up after `visible`
  // goes false. Unmounting the moment it hides skips the exit animation, which
  // is what the offline auto-dismiss used to do — it vanished abruptly while
  // the reconnect path slid away properly.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reconnected, setReconnected] = useState(false);
  const wasOfflineRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!online) {
      // Going offline — show toast then auto-dismiss after 5s
      wasOfflineRef.current = true;
      setReconnected(false);
      setMounted(true);
      setVisible(true);
      timerRef.current = setTimeout(() => {
        setVisible(false);
        timerRef.current = setTimeout(() => {
          setMounted(false);
        }, 300); // Wait for slide-up animation
      }, 5000);
    } else if (wasOfflineRef.current) {
      // Coming back online after being offline
      setReconnected(true);
      setMounted(true);
      setVisible(true);
      timerRef.current = setTimeout(() => {
        setVisible(false);
        timerRef.current = setTimeout(() => {
          setMounted(false);
          setReconnected(false);
          wasOfflineRef.current = false;
        }, 300); // Wait for slide-up animation
      }, 2000); // Show "Back online" for 2s
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [online]);

  if (!mounted) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 font-semibold text-sm flex items-center justify-center shadow-md rounded-full transition-all duration-300 ease-in-out ${
        visible ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'
      } ${reconnected ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}
      style={{ pointerEvents: 'none' }}
    >
      {reconnected ? (
        <>
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span>Back online</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>You&apos;re offline</span>
        </>
      )}
    </div>
  );
}
